import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Presentation, ExternalLink, Download, FileText, Maximize2, Lock, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PresentationPage() {
  const { user, token } = useAuth();
  const [fullscreen, setFullscreen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [stats, setStats] = useState(null);
  const presentationUrl = '/presentation_capex.html';
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (user?.role !== 'super_admin' || !token) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/presentation/stats`, { headers });
        if (r.ok) setStats(await r.json());
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const downloadLivePptx = async () => {
    setDownloading(true);
    try {
      const r = await fetch(`${API}/api/presentation/pptx`, { headers });
      if (!r.ok) {
        toast.error('Échec de la génération du PPTX');
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `CAPEX_Wasabi_Presentation_${today}.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PPTX généré avec vos chiffres LIVE');
    } catch (e) {
      toast.error('Erreur réseau');
    } finally {
      setDownloading(false);
    }
  };

  // Restreint super_admin only
  if (user?.role !== 'super_admin') {
    return (
      <AppLayout>
        <div className="max-w-md mx-auto mt-24 text-center p-8" data-testid="presentation-locked">
          <Lock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Accès réservé</h2>
          <p className="text-sm text-slate-500">
            Cette présentation est destinée à la Direction et n'est accessible qu'aux super-administrateurs.
          </p>
        </div>
      </AppLayout>
    );
  }

  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-50 bg-black" data-testid="presentation-fullscreen">
        <button
          onClick={() => setFullscreen(false)}
          className="absolute top-4 left-4 z-10 bg-white/90 hover:bg-white text-black px-4 py-2 rounded-full text-sm font-semibold shadow-lg"
          data-testid="exit-fullscreen-btn"
        >
          ← Quitter le mode plein écran
        </button>
        <iframe
          src={presentationUrl}
          title="Présentation CAPEX"
          className="w-full h-full border-0"
          data-testid="presentation-iframe-fullscreen"
        />
      </div>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-6 space-y-6" data-testid="presentation-page">
        {/* Header card */}
        <Card className="overflow-hidden border-0 shadow-lg">
          <div className="bg-gradient-to-br from-black via-zinc-900 to-zinc-800 text-white p-8 sm:p-10 relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative">
              <div className="flex items-center gap-2 text-orange-400 text-xs uppercase tracking-widest font-semibold mb-3">
                <Presentation className="w-4 h-4" />
                Présentation Direction · v1.0
              </div>
              <h1 className="text-3xl sm:text-5xl font-extrabold mb-3 tracking-tight">
                CAPEX By Wasabi
              </h1>
              <p className="text-zinc-300 text-base sm:text-lg max-w-2xl">
                Présentation complète à destination de vos hauts dirigeants — 10 modules métier,
                5 profils, bénéfices business chiffrés, roadmap.
              </p>

              <div className="flex flex-wrap gap-3 mt-6">
                <Button
                  size="lg"
                  onClick={() => setFullscreen(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white gap-2 shadow-lg"
                  data-testid="open-fullscreen-btn"
                >
                  <Maximize2 className="w-4 h-4" />
                  Ouvrir en plein écran (mode présentation)
                </Button>
                <a href={presentationUrl} target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    variant="outline"
                    className="gap-2 bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white"
                    data-testid="open-new-tab-btn"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Nouvel onglet
                  </Button>
                </a>
                <Button
                  size="lg"
                  onClick={downloadLivePptx}
                  disabled={downloading}
                  className="gap-2 bg-orange-500/90 hover:bg-orange-500 text-white border-2 border-orange-300 font-semibold"
                  data-testid="download-pptx-live-btn"
                >
                  {downloading
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Download className="w-4 h-4" />}
                  Télécharger PPTX <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded ml-1">LIVE</span>
                </Button>
                <a href={presentationUrl} download="CAPEX_Wasabi_Presentation.html">
                  <Button
                    size="lg"
                    variant="outline"
                    className="gap-2 bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white"
                    data-testid="download-html-btn"
                  >
                    <Download className="w-4 h-4" />
                    HTML
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </Card>

        {/* Quick actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ActionCard
            icon={<Maximize2 className="w-5 h-5" />}
            title="Mode présentation"
            desc="Plein écran sans sidebar pour projeter en comité direction."
            cta="Lancer"
            onClick={() => setFullscreen(true)}
            testid="action-fullscreen"
          />
          <ActionCard
            icon={<FileText className="w-5 h-5" />}
            title="Exporter en PDF"
            desc="Cliquez « Imprimer / PDF » dans la présentation pour sauver en PDF (Ctrl+P)."
            cta="Imprimer"
            onClick={() => window.open(presentationUrl, '_blank')}
            testid="action-print"
          />
          <ActionCard
            icon={<Download className="w-5 h-5" />}
            title="Format PowerPoint LIVE"
            desc="Slides paysage 16:9 avec vos chiffres en direct depuis la base. Régénéré à chaque clic."
            cta="Télécharger .pptx"
            onClick={downloadLivePptx}
            testid="action-pptx-live"
          />
        </div>

        {/* Live stats preview */}
        {stats && (
          <Card className="overflow-hidden border-2 border-orange-200 shadow-md" data-testid="live-stats-card">
            <div className="bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 px-5 py-3 border-b border-orange-200 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-sm font-bold text-orange-900">
                <RefreshCw className="w-4 h-4 text-orange-600" />
                Aperçu des chiffres LIVE qui seront intégrés au PPTX
              </div>
              <span className="text-xs text-orange-700 font-mono">{stats.generated_at}</span>
            </div>
            <CardContent className="p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-orange-600 mb-2">🎯 Demandes CAPEX</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <StatBlock label="Demandes totales" value={stats.sim_total} color="orange" />
                <StatBlock label="GO validés" value={stats.sim_go} color="green" />
                <StatBlock label="NO GO" value={stats.sim_nogo} color="red" />
                <StatBlock label="Contrats signés" value={stats.sim_signed} color="orange-deep" />
              </div>
              <div className="text-xs font-bold uppercase tracking-wider text-orange-600 mb-2">⚙️ Modules opérationnels</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatBlock label="Info d'Or" value={stats.golden_total} subtitle={`${stats.golden_rdv} RDV pris`} />
                <StatBlock label="Pilotage Chantier" value={stats.chantier_total} subtitle={`${stats.chantier_valide} validés`} />
                <StatBlock label="Commandes Agenceur" value={stats.dossier_total} subtitle={`${stats.dossier_valide} validés`} />
                <StatBlock label="PV de Livraison" value={stats.pv_total} subtitle={`${stats.pv_signed} signés`} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Inline preview */}
        <Card className="overflow-hidden border-2 border-orange-200 shadow-md">
          <div className="bg-orange-50 px-4 py-3 border-b border-orange-200 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-orange-900">
              <Presentation className="w-4 h-4" />
              Aperçu intégré
            </div>
            <span className="text-xs text-orange-700">Conseil : utilisez le mode plein écran pour la projection</span>
          </div>
          <iframe
            src={presentationUrl}
            title="Aperçu présentation CAPEX"
            className="w-full border-0"
            style={{ height: '70vh' }}
            data-testid="presentation-iframe-preview"
          />
        </Card>

        {/* Contenu summary */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-500" />
              Ce que contient la présentation
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm text-slate-600">
              <div>✓ Vision & enjeux stratégiques Wasabi</div>
              <div>✓ Architecture & sécurité (stack, rôles, intégrations)</div>
              <div>✓ Cartographie des 10 modules métier</div>
              <div>✓ Simulation CAPEX (workflow + garde-fous)</div>
              <div>✓ Info d'Or & Annonce ALL</div>
              <div>✓ Pilotage Chantier & PV de Livraison</div>
              <div>✓ Commande Corner & Dossier Agenceur</div>
              <div>✓ Prérequis Techniques & Planning</div>
              <div>✓ Business Commercial (7 KPIs + carnet bouillant)</div>
              <div>✓ Vue User vs Vue Admin</div>
              <div>✓ 8 bénéfices business chiffrés</div>
              <div>✓ Roadmap court & moyen terme</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}

function StatBlock({ label, value, subtitle, color = 'orange' }) {
  const palette = {
    orange: { num: 'text-orange-600', border: 'border-orange-300' },
    green: { num: 'text-emerald-600', border: 'border-emerald-300' },
    red: { num: 'text-red-600', border: 'border-red-300' },
    'orange-deep': { num: 'text-orange-700', border: 'border-orange-400' },
  };
  const p = palette[color] || palette.orange;
  return (
    <div className={`bg-white rounded-lg border ${p.border} p-3`} data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className={`text-3xl font-extrabold ${p.num} font-mono leading-none mt-1`}>{value}</div>
      {subtitle && <div className="text-[11px] text-slate-500 mt-1">{subtitle}</div>}
    </div>
  );
}

function ActionCard({ icon, title, desc, cta, onClick, href, testid }) {
  const inner = (
    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full" data-testid={testid}>
      <CardContent className="p-5">
        <div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center mb-3">
          {icon}
        </div>
        <h4 className="font-bold text-slate-800 mb-1">{title}</h4>
        <p className="text-xs text-slate-500 mb-3">{desc}</p>
        <span className="text-sm font-semibold text-orange-600">{cta} →</span>
      </CardContent>
    </Card>
  );
  if (href) {
    return <a href={href} target="_blank" rel="noopener noreferrer" download>{inner}</a>;
  }
  return <div onClick={onClick}>{inner}</div>;
}
